/**
 * 知识库管理（路由 /myse/knowledge-base）
 * UI 严格对齐 web后台.html PAGE: 知识库管理（line 1380-1580）
 *
 * 区域：
 *  1. 顶部 header：标题 + 副标题 + [导出][新增知识]
 *  2. 4 个统计卡片：知识库检查项 / 已启用 / 已停用 / 覆盖机型数
 *  3. 筛选栏 6 列：关键字 / 检查项 / 整机机型 / 时间周期 / 日期范围 / 重置
 *  4. 表格 8 列：检查项 / 覆盖机型 / 最低图像数量 / 规则 / 水印要求 / 操作用户名 / 更新时间 / 状态 / 操作（v2: 移除"水印"内嵌 Tag）
 *  5. 底部说明 + 分页
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Button,
  DatePicker,
  Input,
  Layout,
  Pagination,
  Select,
  Spin,
  Table,
  Tooltip,
  message
} from 'antd'
import {
  AppstoreOutlined,
  BookOutlined,
  CheckCircleOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  StopOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import {
  KnowledgeListItem,
  KnowledgeListParams,
  KnowledgeStatsResponse,
  KnowledgeStatus,
  fetchKnowledgeFilterOptions,
  fetchKnowledgeList,
  fetchKnowledgeStats,
  startKnowledgeExport
} from '../api/knowledge'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import { useIsMasterAdmin } from '../../../app/hooks'
import ExportModal from '../shared/ExportModal'
import StatusTag from '../shared/StatusTag'
import './index.scss'
import EditDrawer from './EditDrawer'
import ConfirmModal from './ConfirmModal'

const { Content } = Layout
const { Option } = Select
const { RangePicker } = DatePicker

interface FilterState {
  keyword: string
  inspection: string
  machine_model: string
  period: string
  start_time: string
  end_time: string
  status: KnowledgeStatus | null
  page: number
  page_size: number
}

const initialFilters: FilterState = {
  keyword: '',
  inspection: '',
  machine_model: '',
  period: '',
  start_time: '',
  end_time: '',
  status: null,
  page: 1,
  page_size: 20
}

const initialStats: KnowledgeStatsResponse = {
  total_knowledges: 0,
  active_knowledges: 0,
  disabled_knowledges: 0,
  total_machine_models: 0
}

// 4 个统计卡片，对齐 web后台 line 1460-1463
const STATS_CARDS: Array<{
  key: keyof KnowledgeStatsResponse
  label: string
  icon: React.ComponentType<any>
  color: string
  bg: string
}> = [
  {
    key: 'total_knowledges',
    label: '知识库检查项',
    icon: BookOutlined,
    color: '#3B7DEB',
    bg: '#E9F2FF'
  },
  {
    key: 'active_knowledges',
    label: '已启用',
    icon: CheckCircleOutlined,
    color: '#10A66B',
    bg: '#E7F8EF'
  },
  {
    key: 'disabled_knowledges',
    label: '已停用',
    icon: StopOutlined,
    color: '#9CA3AF',
    bg: '#F1F2F4'
  },
  {
    key: 'total_machine_models',
    label: '覆盖机型数',
    icon: AppstoreOutlined,
    color: '#FF8B3D',
    bg: '#FFF4EC'
  }
]

// 时间周期选项（接口暂未返回，按 web后台 mock）
const PERIODS = ['每天', '每周', '每月', '每季度', '每年', '一次性']

const KnowledgeBasePage: React.FC = () => {
  const dispatch = useAppDispatch()
  const reduxState = useAppSelector((state) => state)
  const navigate = useNavigate()
  // 权限：仅主管理员可见 新增知识 按钮 + 操作列（编辑 / 开启-关闭）
  const isMasterAdmin = useIsMasterAdmin()
  const [filters, setFilters] = useState<FilterState>(initialFilters)
  const [stats, setStats] = useState<KnowledgeStatsResponse>(initialStats)
  const [list, setList] = useState<KnowledgeListItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [statsLoading, setStatsLoading] = useState(false)
  const [exportOpen, setExportOpen] = useState(false)
  const [machineModelOptions, setMachineModelOptions] = useState<string[]>([])

  const [editing, setEditing] = useState<{
    knowledgeId?: number
    visible: boolean
  }>({
    visible: false
  })
  const [confirmTarget, setConfirmTarget] = useState<{
    row: KnowledgeListItem
    action: 'enable' | 'disable'
  } | null>(null)

  const apiParams: KnowledgeListParams = useMemo(
    () => ({
      inspection: filters.inspection || filters.keyword || '',
      machine_model: filters.machine_model || '',
      start_time: filters.start_time || '',
      end_time: filters.end_time || '',
      status: filters.status ?? null,
      page: filters.page,
      page_size: filters.page_size
    }),
    [filters]
  )

  const loadStats = useCallback(async () => {
    setStatsLoading(true)
    try {
      const s = await fetchKnowledgeStats(dispatch)
      setStats(s)
    } catch (e) {
      console.warn('[knowledge] 统计加载失败:', e)
    } finally {
      setStatsLoading(false)
    }
  }, [dispatch])

  const loadList = useCallback(async () => {
    setLoading(true)
    try {
      const data = await fetchKnowledgeList(dispatch, apiParams)
      setList(data.list || [])
      setTotal(data.total || 0)
    } catch (e) {
      message.error((e as Error)?.message || '加载列表失败')
    } finally {
      setLoading(false)
    }
  }, [dispatch, apiParams])

  const loadFilterOptions = useCallback(async () => {
    try {
      const data = await fetchKnowledgeFilterOptions(dispatch)
      setMachineModelOptions(data.machine_model_options || [])
    } catch (e) {
      console.warn('[knowledge] 筛选选项加载失败:', e)
    }
  }, [dispatch])

  useEffect(() => {
    loadStats()
    loadFilterOptions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    loadList()
  }, [loadList])

  const refreshAll = useCallback(() => {
    loadList()
    loadStats()
  }, [loadList, loadStats])

  const handleFilterChange = useCallback((patch: Partial<FilterState>) => {
    setFilters((prev) => ({ ...prev, ...patch, page: 1 }))
  }, [])

  const handleReset = useCallback(() => {
    setFilters(initialFilters)
  }, [])

  const handleExportClick = useCallback(async () => {
    const groupId = reduxState?.group?.groupId || ''
    const userId = reduxState?.main?.user?.user_id || ''
    if (!groupId || !userId) {
      message.error('未获取到团队或用户信息')
      return
    }
    try {
      await startKnowledgeExport(dispatch, {
        inspection: filters.inspection || filters.keyword || '',
        machine_model: filters.machine_model || '',
        start_time: filters.start_time || '',
        end_time: filters.end_time || '',
        status: filters.status ?? null,
        group_id: groupId,
        user_id: userId
      })
      setExportOpen(true)
    } catch (e) {
      message.error((e as Error)?.message || '导出失败')
    }
  }, [dispatch, reduxState, filters])

  // 检查项选项 = 当前已加载列表的检查项名（去重）
  const inspectionOptions = useMemo(() => {
    return Array.from(new Set(list.map((r) => r.inspection).filter(Boolean)))
  }, [list])

  // group_id/user_id
  const groupId = reduxState?.group?.groupId || ''
  const userId = reduxState?.main?.user?.user_id || ''

  const columns = useMemo(
    () => [
      {
        title: '检查项',
        dataIndex: 'inspection',
        key: 'inspection',
        width: 200,
        ellipsis: true,
        render: (text: string) => (
          <Tooltip title={text}>
            <span style={{ color: '#1F2937', fontWeight: 500 }}>
              {text || '-'}
            </span>
          </Tooltip>
        )
      },
      {
        title: '覆盖机型',
        dataIndex: 'machine_model',
        key: 'machine_model',
        width: 240,
        render: (models: string[]) => {
          const arr = Array.isArray(models) ? models : []
          const visible = arr.slice(0, 2)
          const rest = arr.length - visible.length
          return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {visible.map((m) => (
                <Tooltip key={m} title={m}>
                  <span
                    className="myse-soft-tag myse-soft-tag--blue"
                    style={{
                      maxWidth: 120,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontFamily: 'Inter, system-ui'
                    }}
                  >
                    {m}
                  </span>
                </Tooltip>
              ))}
              {rest > 0 && (
                <Tooltip title={arr.slice(2).join('、')}>
                  <span className="myse-soft-tag myse-soft-tag--slate">
                    +{rest}
                  </span>
                </Tooltip>
              )}
              {arr.length === 0 && <span style={{ color: '#D9D9D9' }}>-</span>}
            </div>
          )
        }
      },
      {
        title: '最低图像数量',
        dataIndex: 'media_num',
        key: 'media_num',
        width: 120,
        render: (mn: KnowledgeListItem['media_num']) => (
          <span style={{ fontFamily: 'Inter, system-ui' }}>
            <span style={{ fontWeight: 600, color: '#1F2937' }}>
              {mn?.photo ?? 0}
            </span>
            <span style={{ color: '#9CA3AF', marginLeft: 4 }}>张</span>
          </span>
        )
      },
      {
        title: '规则',
        dataIndex: 'rule_count',
        key: 'rules',
        width: 120,
        render: (n: number) => (
          <span className="myse-soft-tag myse-soft-tag--orange">
            {n ?? 0} 条规则
          </span>
        )
      },
      {
        title: '水印要求',
        dataIndex: 'watermark_rule',
        key: 'watermark_rule',
        width: 220,
        ellipsis: true,
        render: (text: string) => (
          <Tooltip title={text || '-'}>
            <span style={{ color: '#4B5563' }}>{text || '-'}</span>
          </Tooltip>
        )
      },
      {
        title: '操作用户名',
        dataIndex: 'operator_name',
        key: 'operator_name',
        width: 100,
        render: (text: string) => (
          <span style={{ color: '#374151' }}>{text || '-'}</span>
        )
      },
      {
        title: '更新时间',
        dataIndex: 'update_time',
        key: 'update_time',
        width: 170,
        render: (t: string) => (
          <span
            style={{
              color: '#6B7280',
              fontFamily: 'Inter, system-ui',
              whiteSpace: 'nowrap'
            }}
          >
            {t || '-'}
          </span>
        )
      },
      {
        title: '状态',
        dataIndex: 'status',
        key: 'status',
        width: 80,
        fixed: 'right' as const,
        render: (s: KnowledgeStatus) =>
          s === 1 ? (
            <StatusTag label="启用" color="#10A66B" bg="#E7F8EF" />
          ) : (
            <StatusTag label="停用" color="#9CA3AF" bg="#F1F2F4" />
          )
      },
      // 操作列：仅主管理员可见
      ...(isMasterAdmin
        ? [
            {
              title: '操作',
              key: 'action',
              width: 120,
              fixed: 'right' as const,
              render: (_: any, row: KnowledgeListItem) => (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 12
                  }}
                >
                  <a
                    className="myse-link myse-link--primary"
                    onClick={() =>
                      setEditing({
                        knowledgeId: row.knowledge_id,
                        visible: true
                      })
                    }
                  >
                    编辑
                  </a>
                  <a
                    className={
                      'myse-link ' +
                      (row.status === 1
                        ? 'myse-link--danger'
                        : 'myse-link--success')
                    }
                    onClick={() =>
                      setConfirmTarget({
                        row,
                        action: row.status === 1 ? 'disable' : 'enable'
                      })
                    }
                  >
                    {row.status === 1 ? '停用' : '启用'}
                  </a>
                </span>
              )
            }
          ]
        : [])
    ],
    [isMasterAdmin]
  )

  return (
    <Layout className="kb-page">
      <Content style={{ padding: 24 }}>
        {/* Header */}
        <div className="myse-page-header">
          <div>
            <h1 className="myse-page-title">知识库管理</h1>
            <div className="myse-page-subtitle">
              维护检查项的图像规格、审核要求与覆盖机型，作为 AI
              与人工审核的统一依据。
            </div>
          </div>
          <div className="myse-page-header__actions">
            <Button
              icon={<DownloadOutlined />}
              disabled={!groupId || !userId || total === 0}
              onClick={handleExportClick}
            >
              导出
            </Button>
            {isMasterAdmin && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() =>
                  setEditing({ knowledgeId: undefined, visible: true })
                }
              >
                新增知识
              </Button>
            )}
          </div>
        </div>

        {/* 4 统计卡片 */}
        <Spin spinning={statsLoading} delay={120}>
          <div className="mingyang-stats mingyang-stats--cols-4">
            {STATS_CARDS.map((card) => (
              <div key={card.key} className="mingyang-stats__card">
                <div
                  className="mingyang-stats__icon"
                  style={{ background: card.bg, color: card.color }}
                >
                  <card.icon />
                </div>
                <div className="mingyang-stats__content">
                  <div
                    className="mingyang-stats__value"
                    style={{ color: '#1F2937' }}
                  >
                    {stats[card.key] ?? 0}
                  </div>
                  <div className="mingyang-stats__label">{card.label}</div>
                </div>
              </div>
            ))}
          </div>
        </Spin>

        {/* 筛选栏 6 列 */}
        <div
          className="mingyang-filterbar"
          style={{
            display: 'grid',
            gridTemplateColumns: '1.6fr 1.2fr 1.2fr 1fr 1.6fr auto',
            gap: 10,
            alignItems: 'center'
          }}
        >
          <Input
            value={filters.keyword}
            onChange={(e) => handleFilterChange({ keyword: e.target.value })}
            placeholder="检查项名称 / 规格关键字"
            prefix={<SearchOutlined style={{ color: '#9CA3AF' }} />}
            allowClear
          />
          <Select
            value={filters.inspection || undefined}
            onChange={(v: string) =>
              handleFilterChange({ inspection: v || '' })
            }
            placeholder="检查项"
            allowClear
            showSearch
          >
            {inspectionOptions.map((o) => (
              <Option key={o} value={o}>
                {o}
              </Option>
            ))}
          </Select>
          <Select
            value={filters.machine_model || undefined}
            onChange={(v: string) =>
              handleFilterChange({ machine_model: v || '' })
            }
            placeholder="整机机型"
            allowClear
            showSearch
          >
            {machineModelOptions.map((o) => (
              <Option key={o} value={o}>
                {o}
              </Option>
            ))}
          </Select>
          <Select
            value={filters.period || undefined}
            onChange={(v: string) => handleFilterChange({ period: v || '' })}
            placeholder="时间周期"
            allowClear
          >
            {PERIODS.map((o) => (
              <Option key={o} value={o}>
                {o}
              </Option>
            ))}
          </Select>
          <RangePicker
            value={
              filters.start_time && filters.end_time
                ? [dayjs(filters.start_time), dayjs(filters.end_time)]
                : (null as any)
            }
            onChange={(dates) => {
              if (!dates || !dates[0]) {
                handleFilterChange({ start_time: '', end_time: '' })
              } else {
                handleFilterChange({
                  start_time: dates[0] ? dates[0].format('YYYY-MM-DD') : '',
                  end_time: dates[1] ? dates[1].format('YYYY-MM-DD') : ''
                })
              }
            }}
            format="YYYY-MM-DD"
            style={{ width: '100%' }}
          />
          <a className="myse-reset-btn" onClick={handleReset}>
            <ReloadOutlined />
            重置
          </a>
        </div>

        {/* 表格 */}
        <div className="mingyang-table-wrapper">
          <Table
            rowKey="knowledge_id"
            loading={loading}
            columns={columns}
            dataSource={list}
            rowClassName={(row: KnowledgeListItem) =>
              row.status === 0 ? 'myse-row-inactive' : ''
            }
            scroll={{ x: 1500 }}
            pagination={false}
            size="middle"
          />
          <div className="mingyang-table-footer mingyang-table-footer--split">
            <span className="kb-footnote">
              <ExclamationCircleOutlined
                style={{ color: '#F59E0B', marginRight: 6 }}
              />
              知识库内检查项不可删除，可通过&quot;停用&quot;关闭采集与审核流程。
            </span>
            <Pagination
              size="small"
              current={filters.page}
              pageSize={filters.page_size}
              total={total}
              showSizeChanger
              pageSizeOptions={['20', '50', '100']}
              showTotal={(n) => `共 ${n} 条`}
              onChange={(p, s) =>
                setFilters((prev) => ({
                  ...prev,
                  page: p,
                  page_size: s || 20
                }))
              }
              onShowSizeChange={(_, s) =>
                setFilters((prev) => ({ ...prev, page: 1, page_size: s }))
              }
            />
          </div>
        </div>

        {/* 添加/编辑抽屉 */}
        <EditDrawer
          visible={editing.visible}
          knowledgeId={editing.knowledgeId}
          dispatch={dispatch}
          state={reduxState}
          onClose={() => setEditing({ visible: false })}
          onSuccess={() => {
            setEditing({ visible: false })
            refreshAll()
          }}
        />

        {/* 停用/启用确认 */}
        <ConfirmModal
          target={confirmTarget}
          dispatch={dispatch}
          state={reduxState}
          onClose={() => setConfirmTarget(null)}
          onSuccess={() => {
            setConfirmTarget(null)
            refreshAll()
          }}
        />

        {/* 导出提示 */}
        <ExportModal
          visible={exportOpen}
          onClose={() => setExportOpen(false)}
          onGoToDownloads={() => navigate('/myse/downloads')}
        />
      </Content>
    </Layout>
  )
}

export default KnowledgeBasePage
